// Performance utilities for Core Web Vitals optimization

/**
 * Defers loading of non-critical CSS
 * @param {string} href - CSS file URL
 * @param {string} media - Media query for the CSS (default: 'all')
 */
export const loadDeferredCSS = (href, media = 'all') => {
  const link = document.createElement('link');
  link.rel = 'stylesheet';
  link.href = href;
  link.media = 'print'; // Load as print initially to avoid blocking
  link.onload = function() {
    this.media = media; // Switch to target media query when loaded
  };
  document.head.appendChild(link);
  
  // Fallback for browsers that don't support onload on link elements
  setTimeout(() => {
    if (link.media === 'print') {
      link.media = media;
    }
  }, 100);
};

/**
 * Defers loading of third-party scripts
 * @param {string} src - Script URL
 * @param {Function} callback - Optional callback when script loads
 * @param {number} delay - Delay before loading (default: 0)
 */
export const loadDeferredScript = (src, callback, delay = 0) => {
  setTimeout(() => {
    const script = document.createElement('script');
    script.src = src;
    script.async = true;
    
    if (callback) {
      script.onload = callback;
    }
    
    document.head.appendChild(script);
  }, delay);
};

/**
 * Preloads critical images for better LCP
 * @param {Array} imageSources - Array of image URLs to preload
 */
export const preloadImages = (imageSources) => {
  imageSources.forEach((src, index) => {
    const link = document.createElement('link');
    link.rel = 'preload';
    link.as = 'image';
    link.href = src;
    
    // Set fetchpriority high for the first image (likely LCP)
    if (index === 0) {
      link.setAttribute('fetchpriority', 'high');
    }
    
    document.head.appendChild(link);
  });
};

/**
 * Get network information for adaptive loading
 */
export const getNetworkInfo = () => {
  if ('connection' in navigator) {
    const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
    return {
      effectiveType: connection.effectiveType, // '4g', '3g', '2g', 'slow-2g'
      saveData: connection.saveData || false,
      downlink: connection.downlink || 10,
      rtt: connection.rtt || 50
    };
  }
  return { effectiveType: '4g', saveData: false, downlink: 10, rtt: 50 };
};

/**
 * Get optimal image quality based on network conditions
 */
export const getOptimalImageQuality = () => {
  const network = getNetworkInfo();
  if (network.saveData || network.effectiveType === 'slow-2g') return 'auto:eco';
  if (network.effectiveType === '2g') return 'auto:low';
  if (network.effectiveType === '3g') return 'auto:good';
  return 'auto:best';
};

/**
 * Optimizes images with lazy loading and mobile-aware sizing
 * @param {HTMLImageElement} img - Image element
 * @param {Object} options - Optimization options
 */
export const optimizeImage = (img, options = {}) => {
  const {
    loading = 'lazy',
    decoding = 'async',
    fetchpriority = 'auto'
  } = options;
  
  const isMobile = window.innerWidth <= 768;
  const network = getNetworkInfo();
  
  // Set loading attributes
  img.loading = loading;
  img.decoding = decoding;
  
  if (fetchpriority !== 'auto') {
    img.setAttribute('fetchpriority', fetchpriority);
  }
  
  // Mobile-specific optimizations
  if (isMobile) {
    // More aggressive lazy loading on mobile
    const rootMargin = network.effectiveType === '2g' ? '20px' : '50px';
    
    if ('IntersectionObserver' in window && loading === 'lazy') {
      const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.classList.add('defer-loading', 'loaded');
            observer.unobserve(entry.target);
          }
        });
      }, {
        rootMargin: rootMargin,
        threshold: 0.1
      });
      
      observer.observe(img);
    }
  } else {
    // Desktop optimization
    if ('IntersectionObserver' in window && loading === 'lazy') {
      const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.classList.add('defer-loading', 'loaded');
            observer.unobserve(entry.target);
          }
        });
      }, {
        rootMargin: '50px'
      });
      
      observer.observe(img);
    }
  }
};

/**
 * Measures and reports Core Web Vitals
 */
export const measureCoreWebVitals = () => {
  // LCP (Largest Contentful Paint)
  if ('PerformanceObserver' in window) {
    const observer = new PerformanceObserver((list) => {
      const entries = list.getEntries();
      const lastEntry = entries[entries.length - 1];
      
      console.log('LCP:', lastEntry.startTime);
      
      // Send to analytics if available
      if (window.gtag) {
        window.gtag('event', 'web_vitals', {
          name: 'LCP',
          value: Math.round(lastEntry.startTime),
          event_category: 'performance'
        });
      }
    });
    
    observer.observe({ entryTypes: ['largest-contentful-paint'] });
  }
  
  // FID (First Input Delay) - modern replacement with INP
  if ('PerformanceObserver' in window) {
    const observer = new PerformanceObserver((list) => {
      list.getEntries().forEach((entry) => {
        console.log('INP:', entry.processingStart - entry.startTime);
        
        if (window.gtag) {
          window.gtag('event', 'web_vitals', {
            name: 'INP',
            value: Math.round(entry.processingStart - entry.startTime),
            event_category: 'performance'
          });
        }
      });
    });
    
    observer.observe({ entryTypes: ['event'] });
  }
  
  // CLS (Cumulative Layout Shift)
  if ('PerformanceObserver' in window) {
    let clsValue = 0;
    
    const observer = new PerformanceObserver((list) => {
      list.getEntries().forEach((entry) => {
        if (!entry.hadRecentInput) {
          clsValue += entry.value;
        }
      });
      
      console.log('CLS:', clsValue);
      
      if (window.gtag) {
        window.gtag('event', 'web_vitals', {
          name: 'CLS',
          value: Math.round(clsValue * 1000),
          event_category: 'performance'
        });
      }
    });
    
    observer.observe({ entryTypes: ['layout-shift'] });
  }
};

/**
 * Optimizes touch interactions for mobile devices
 */
export const optimizeTouchInteractions = () => {
  if (!('ontouchstart' in window)) return; // Not a touch device
  
  // Add passive listeners for better performance
  const addPassiveListener = (element, event, handler) => {
    if (element && element.addEventListener) {
      element.addEventListener(event, handler, { passive: true });
    }
  };
  
  // Debounce function for touch events
  const debounce = (func, wait) => {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  };
  
  // Pre-load critical resources on touch start
  const prefetchOnTouch = debounce((e) => {
    const target = e.target.closest('a, button');
    if (target && target.href && !target.href.startsWith('mailto:') && !target.href.startsWith('tel:')) {
      const link = document.createElement('link');
      link.rel = 'prefetch';
      link.href = target.href;
      document.head.appendChild(link);
    }
  }, 50);
  
  // Add touch event listeners
  addPassiveListener(document, 'touchstart', prefetchOnTouch);
  
  // Add haptic feedback for supported devices
  const addHapticFeedback = (element) => {
    if ('vibrate' in navigator && element) {
      element.addEventListener('touchstart', () => {
        navigator.vibrate(10); // Short vibration
      }, { passive: true });
    }
  };
  
  // Apply haptic feedback to buttons
  document.querySelectorAll('button, .btn-primary, .btn-secondary').forEach(addHapticFeedback);
};

/**
 * Progressive enhancement loader with mobile optimizations
 * Loads enhanced features after critical content is rendered
 */
export const loadProgressiveEnhancements = () => {
  const isMobile = window.innerWidth <= 768;
  const network = getNetworkInfo();
  
  // Wait for critical content to load
  window.addEventListener('load', () => {
    const baseDelay = network.effectiveType === '2g' ? 500 : 100;
    
    setTimeout(() => {
      // Load non-critical animations
      const deferredElements = document.querySelectorAll('.defer-loading');
      deferredElements.forEach((element, index) => {
        setTimeout(() => {
          element.classList.add('loaded');
        }, index * (isMobile ? 150 : 100)); // Slower stagger on mobile
      });
      
      // Initialize touch optimizations on mobile
      if (isMobile) {
        optimizeTouchInteractions();
      }
      
    }, baseDelay);
  });
};

/**
 * Resource hints for better performance
 */
export const addResourceHints = () => {
  const resourceHints = [
    { rel: 'dns-prefetch', href: '//fonts.gstatic.com' },
    { rel: 'dns-prefetch', href: '//www.google-analytics.com' },
    { rel: 'dns-prefetch', href: '//www.googletagmanager.com' },
    { rel: 'preconnect', href: 'https://res.cloudinary.com', crossorigin: true }
  ];
  
  resourceHints.forEach(hint => {
    const link = document.createElement('link');
    Object.keys(hint).forEach(key => {
      if (key === 'crossorigin') {
        link.crossOrigin = hint[key];
      } else {
        link.setAttribute(key, hint[key]);
      }
    });
    document.head.appendChild(link);
  });
};

// Initialize performance optimizations
export const initPerformanceOptimizations = () => {
  // Measure Core Web Vitals
  measureCoreWebVitals();
  
  // Load progressive enhancements
  loadProgressiveEnhancements();
  
  // Add resource hints if not already present
  if (!document.querySelector('[rel="dns-prefetch"]')) {
    addResourceHints();
  }
};